import { View, StyleSheet } from "react-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { Bot } from "lucide-react";
import { Info } from "@phosphor-icons/react/dist/csr/Info";
import { Upload } from "lucide-react";
import AdminCompanionDocumentCardList from "../../components/adminDashboard/AdminCompanionDocumentCardList/AdminCompanionDocumentCardList";
import {
  DocumentStatus as ApiDocumentStatus,
  StoragePathType,
  useGetDocumentLazyQuery,
  useDeleteAdminDocumentMutation,
  useGetAdminDocumentsForTenantQuery,
  useCreateAdminDocumentMutation,
} from "../../graphql/generated/graphql";
import useFileUpload, { ProcessedFile } from "../../hooks/useFileUpload";
import { formatSubmittedDate } from "../../utils/date";
import { DocumentData } from "../../components/adminDashboard/AdminCompanionDocumentCard/AdminCompanionDocumentCard";

const POLL_INTERVAL_MS = 4000;

const mapApiStatusToUi = (status: ApiDocumentStatus): DocumentData["status"] => {
  switch (status) {
    case ApiDocumentStatus.Completed:
      return "ready";
    case ApiDocumentStatus.Failed:
      return "failed";
    case ApiDocumentStatus.Pending:
    case ApiDocumentStatus.Processing:
    default:
      return "processing";
  }
};

const mapAdminDocumentToCard = (document: {
  id: string;
  title?: string | null;
  status: ApiDocumentStatus;
  created?: string | null;
  files?: Array<{ id: string; fileName?: string | null }> | null;
}): DocumentData => {
  const primaryFile = document.files?.[0];
  return {
    id: document.id,
    documentId: document.id,
    fileId: primaryFile?.id,
    filename: primaryFile?.fileName || document.title || "Document",
    size: "Unknown size",
    uploadedDate: formatSubmittedDate(document.created),
    status: mapApiStatusToUi(document.status),
  };
};

function AdminCompanionSettingsContainer() {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [isUploadHovered, setIsUploadHovered] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [createAdminDocument] = useCreateAdminDocumentMutation();
  const [deleteAdminDocument] = useDeleteAdminDocumentMutation();
  const [getDocument] = useGetDocumentLazyQuery({
    fetchPolicy: "network-only",
  });
  const { data: adminDocumentsData, refetch: refetchAdminDocuments } = useGetAdminDocumentsForTenantQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setDocuments(
      (adminDocumentsData?.getAdminDocumentsForTenant?.data || []).map((document) => mapAdminDocumentToCard(document))
    );
  }, [adminDocumentsData]);

  const handleAfterUpload = useCallback(
    async (processedFiles: ProcessedFile[]) => {
      const file = processedFiles?.[0];
      if (!file) return;

      const displayName = file.file.fileName || "Document";

      await refetchAdminDocuments();

      try {
        const result = await createAdminDocument({
          variables: {
            fileId: file.file.id,
            title: displayName,
          },
        });

        const created = result.data?.createAdminDocument?.data;
        if (!result.data?.createAdminDocument?.success || !created?.id) {
          throw new Error(result.data?.createAdminDocument?.message || "Failed to create admin document.");
        }
        await refetchAdminDocuments();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed.";
        setUploadError(message);
      }
    },
    [createAdminDocument, refetchAdminDocuments]
  );

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.AdminDocuments,
    multiple: false,
    beforeUpload: () => setUploadError(""),
    afterUpload: handleAfterUpload,
    onError: (message) => setUploadError(message || "Upload failed."),
  });

  const handleAddFile = useCallback(async () => {
    try {
      await handleFileUpload();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed.";
      setUploadError(message);
    }
  }, [handleFileUpload]);

  const documentsToPoll = useMemo(
    () => documents.filter((doc) => doc.documentId && doc.status !== "ready" && doc.status !== "failed"),
    [documents]
  );

  useEffect(() => {
    if (documentsToPoll.length === 0) return;
    let isActive = true;

    const pollStatuses = async () => {
      const results = await Promise.all(
        documentsToPoll.map(async (doc) => {
          try {
            const response = await getDocument({ variables: { id: doc.documentId } });
            return { documentId: doc.documentId, data: response.data?.getDocument?.data };
          } catch {
            return { documentId: doc.documentId, data: null };
          }
        })
      );

      if (!isActive) return;

      await refetchAdminDocuments();
    };

    pollStatuses();
    const interval = setInterval(pollStatuses, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [documentsToPoll, getDocument]);

  const handleDelete = useCallback(
    async (id: string) => {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === id
            ? {
                ...doc,
                status: "deleting",
                statusText: "Deleting...",
              }
            : doc
        )
      );

      try {
        const result = await deleteAdminDocument({ variables: { id } });
        if (!result.data?.deleteAdminDocument?.success) {
          throw new Error(result.data?.deleteAdminDocument?.message || "Failed to delete document.");
        }
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete document.";
        setUploadError(message);
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  status: "ready",
                  statusText: undefined,
                }
              : doc
          )
        );
      }
    },
    [deleteAdminDocument, documents]
  );

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Bot size={20} color="#7C3AED" />
          </View>
          <View style={styles.headerContent}>
            <CustomText
              text="Support Knowledge Base"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.title}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Upload documents to power the AI Companion's 'Support' chat. Students can ask questions about policies, procedures, and institutional information."
              size="s"
              useTranslationText={false}
              style={styles.description}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* Upload Area */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload documents for Support knowledge base. Accepts PDF, DOCX, TXT files up to 25MB each."
          className="upload-area"
          style={{
            border: isUploadHovered ? "2px dashed #BF84FC" : "2px dashed #D1D5DB",
            borderRadius: 8,
            padding: 32,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isUploadHovered ? "#faf5ff80" : "#FFFFFF",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
          onClick={handleAddFile}
          onMouseEnter={() => setIsUploadHovered(true)}
          onMouseLeave={() => setIsUploadHovered(false)}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleAddFile();
            }
          }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <Upload size={32} color="#9CA3AF" />
            <div>
              <View style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <CustomText
                  text="Drop files here or click to upload"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#374151", marginBottom: 4 }}
                />
                <CustomText
                  text="PDF, DOCX, TXT up to 25MB each"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#6B7280", marginBottom: 4 }}
                />
                <CustomText
                  text="Documents are processed and indexed for AI-powered search"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#6B7280" }}
                />
              </View>
            </div>
          </div>
        </div>

        <CustomSpacing type="vertical" size="l" />

        {uploadError ? (
          <>
            <CustomText text={uploadError} size="xs" useTranslationText={false} style={styles.errorText} />
            <CustomSpacing type="vertical" size="s" />
          </>
        ) : null}

        {/* Info Box */}
        <View style={styles.infoBox}>
          <Info size={16} color="#2563EB" weight="regular" style={styles.infoIcon} />
          <View style={styles.infoContent}>
            <CustomText
              text="How it works"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.infoTitle}
            />
            <CustomText
              text="Uploaded documents are processed and indexed, typically taking about 1 minute. Once ready, the AI Companion can answer student questions using this knowledge base."
              size="xs"
              useTranslationText={false}
              style={styles.infoText}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* Document List */}
        <AdminCompanionDocumentCardList documents={documents} onDelete={handleDelete} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 16,
    backgroundColor: "#F3E8FF",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    color: "#111827",
  },
  description: {
    color: "#475467",
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    padding: 12,
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 8,
  },
  infoIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    color: "#1E40AF",
    marginBottom: 4,
  },
  infoText: {
    color: "#1E40AF",
  },
  errorText: {
    color: "#B91C1C",
  },
});

export default AdminCompanionSettingsContainer;
